/* =========================================================================
   Via Roma — theme.js
   The only globally loaded script. Capability loader + Swup lifecycle + every
   behaviour the data-hooks from the sections are waiting on. Vanilla, no build.

   Swup lifecycle guarantees (see initSwup):
     1. On content:replace, destroySections() runs BEFORE initSections() —
        old component instances are torn down before new ones initialise.
     2. ScrollTrigger.refresh() is called after each navigation.
     3. The sticky-mobile-ATC IntersectionObserver is disconnected and
        re-observed against the new DOM after each navigation.
   ========================================================================= */
(function () {
  'use strict';

  var html = document.documentElement;
  var cfg = (window.ViaRoma && window.ViaRoma.config) || {};
  var STR = cfg.strings || {};
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  window.ViaRoma = window.ViaRoma || {};
  window.ViaRoma.sections = window.ViaRoma.sections || {};

  /* ---------- helpers ---------- */
  function $(s, c) { return (c || document).querySelector(s); }
  function $$(s, c) { return Array.prototype.slice.call((c || document).querySelectorAll(s)); }
  function debounce(fn, wait) { var t; return function () { var a = arguments, c = this; clearTimeout(t); t = setTimeout(function () { fn.apply(c, a); }, wait); }; }

  function formatMoney(cents) {
    var value = (cents || 0) / 100;
    var fmt = cfg.moneyFormat || '${{amount}}';
    function comma(n) { return n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','); }
    function noDec(n) { return Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','); }
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (m, name) {
      switch (name) {
        case 'amount_no_decimals': return noDec(value);
        case 'amount_with_comma_separator': return value.toFixed(2).replace('.', ',');
        case 'amount_no_decimals_with_comma_separator': return noDec(value);
        default: return comma(value);
      }
    });
  }

  function toast(msg) {
    var t = document.createElement('div');
    t.className = 'vr-toast';
    t.textContent = msg;
    document.body.appendChild(t);
    requestAnimationFrame(function () { t.classList.add('is-visible'); });
    setTimeout(function () { t.classList.remove('is-visible'); setTimeout(function () { t.remove(); }, 400); }, 2200);
  }

  /* ---------- lazy library loader ---------- */
  var loaded = {};
  function loadScript(url) {
    if (loaded[url]) return loaded[url];
    loaded[url] = new Promise(function (res, rej) {
      var s = document.createElement('script');
      s.src = url; s.defer = true; s.onload = res; s.onerror = rej;
      document.head.appendChild(s);
    });
    return loaded[url];
  }
  function loadStyle(url) {
    if (loaded[url]) return loaded[url];
    var l = document.createElement('link'); l.rel = 'stylesheet'; l.href = url;
    document.head.appendChild(l);
    loaded[url] = Promise.resolve();
    return loaded[url];
  }
  var libDeps = {
    swiper: function () { loadStyle(cfg.cdn.swiperCss); return loadScript(cfg.cdn.swiper); },
    photoswipe: function () { loadStyle(cfg.cdn.photoswipeCss); return Promise.all([loadScript(cfg.cdn.photoswipe), loadScript(cfg.cdn.photoswipeLightbox)]); },
    gsap: function () { return loadScript(cfg.cdn.gsap).then(function () { return loadScript(cfg.cdn.scrollTrigger); }); }
  };
  function ensureLibs(names) { return Promise.all((names || []).map(function (n) { return libDeps[n] ? libDeps[n]() : Promise.resolve(); })); }

  /* ---------- section lifecycle ---------- */
  var activeInstances = [];

  function initSections(scope) {
    $$('[data-section-js]', scope).forEach(function (root) {
      if (root.__vrInited) return;
      var key = root.dataset.sectionJs;
      var reqs = (root.dataset.requires || '').split(/\s+/).filter(Boolean);
      ensureLibs(reqs)
        .then(function () { if (cfg.components && cfg.components[key]) return loadScript(cfg.components[key]); })
        .then(function () {
          var h = window.ViaRoma.sections[key];
          if (h && h.init) { h.init(root); root.__vrInited = true; activeInstances.push({ key: key, root: root }); }
        })
        .catch(function (e) { if (window.console) console.warn('[ViaRoma] init failed:', key, e); });
    });
  }

  function destroySections() {
    activeInstances.forEach(function (inst) {
      var h = window.ViaRoma.sections[inst.key];
      if (h && h.destroy) { try { h.destroy(inst.root); } catch (e) {} }
      inst.root.__vrInited = false;
    });
    activeInstances = [];
  }

  /* ---------- GSAP scroll reveals ---------- */
  function revealFallback(scope) {
    $$('[data-animate], [data-hero-item]', scope).forEach(function (el) { el.classList.add('is-revealed'); el.style.opacity = '1'; el.style.transform = 'none'; });
  }
  function setupReveals(scope) {
    if (!cfg.scrollAnimations || reduce.matches) { return; }
    var els = $$('[data-animate]', scope);
    var heroes = $$('[data-hero-stagger]', scope);
    if (!els.length && !heroes.length) return;
    var safety = setTimeout(function () { if (typeof window.gsap === 'undefined') revealFallback(scope); }, 3000);
    ensureLibs(['gsap']).then(function () {
      clearTimeout(safety);
      if (typeof window.gsap === 'undefined') { revealFallback(scope); return; }
      gsap.registerPlugin(ScrollTrigger);
      if (els.length) {
        ScrollTrigger.batch(els, {
          start: 'top 88%',
          once: true,
          onEnter: function (targets) {
            targets.forEach(function (t) { t.classList.add('is-revealed'); });
            gsap.fromTo(targets, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.7, ease: 'power2.out', stagger: 0.08 });
          }
        });
      }
      heroes.forEach(function (h) {
        var items = $$('[data-hero-item]', h);
        items.forEach(function (t) { t.classList.add('is-revealed'); });
        gsap.fromTo(items, { opacity: 0, y: 24 }, { opacity: 1, y: 0, duration: 0.8, ease: 'power2.out', stagger: 0.2 });
      });
      ScrollTrigger.refresh();
    }).catch(function () { clearTimeout(safety); revealFallback(scope); });
  }
  function refreshScrollTrigger() { if (typeof window.ScrollTrigger !== 'undefined') { try { ScrollTrigger.refresh(); } catch (e) {} } }
  function pruneScrollTriggers() {
    if (typeof window.ScrollTrigger === 'undefined') return;
    ScrollTrigger.getAll().forEach(function (t) { if (t.trigger && !document.contains(t.trigger)) t.kill(); });
  }

  /* ---------- sticky mobile ATC ---------- */
  var stickyObs = null;
  function teardownStickyATC() { if (stickyObs) { stickyObs.disconnect(); stickyObs = null; } }
  function setupStickyATC(scope) {
    var bar = $('[data-pdp-sticky]', scope) || $('[data-pdp-sticky]');
    var anchor = $('[data-pdp-buy]', scope);
    if (!bar || !anchor) return;
    stickyObs = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        bar.classList.toggle('is-visible', !e.isIntersecting);
        bar.setAttribute('aria-hidden', e.isIntersecting ? 'true' : 'false');
      });
    }, { threshold: 0, rootMargin: '0px 0px -10% 0px' });
    stickyObs.observe(anchor);
  }

  /* ---------- expandables ("read more") ---------- */
  function setupExpandables(scope) {
    $$('[data-expandable]', scope).forEach(function (box) {
      if (box.__exp) return; box.__exp = true;
      var content = $('[data-expandable-content]', box) || box;
      requestAnimationFrame(function () {
        if (content.scrollHeight > 110) {
          box.classList.add('is-clamped');
          var btn = document.createElement('button');
          btn.type = 'button'; btn.className = 'expandable__toggle'; btn.textContent = STR.readMore || 'Read more';
          btn.addEventListener('click', function () {
            var clamped = box.classList.toggle('is-clamped');
            btn.textContent = clamped ? (STR.readMore || 'Read more') : (STR.readLess || 'Read less');
          });
          box.appendChild(btn);
        }
      });
    });
  }

  /* ---------- per-scope setup / teardown ---------- */
  function setupScope(scope) {
    initSections(scope);
    setupReveals(scope);
    setupStickyATC(scope);
    setupExpandables(scope);
    markWishlist(scope);
  }

  /* ---------- cart ---------- */
  var cart = {
    drawer: null,
    init: function () {
      this.drawer = $('[data-cart-drawer]');
      var self = this;
      document.addEventListener('click', function (e) {
        if (e.target.closest('[data-cart-toggle]') && cfg.cartType === 'drawer') { e.preventDefault(); self.open(); }
        if (e.target.closest('[data-cart-close]')) { self.close(); }
        var dec = e.target.closest('[data-qty-down]'); var inc = e.target.closest('[data-qty-up]');
        if (dec || inc) { self.step(dec || inc, dec ? -1 : 1); }
        var rm = e.target.closest('[data-cart-remove]');
        if (rm && rm.closest('[data-cart-drawer]')) { e.preventDefault(); self.changeLine(rm.closest('[data-cart-item]'), 0); }
      });
      // Add to cart (PDP form)
      document.addEventListener('submit', function (e) {
        var form = e.target;
        if (form.querySelector && form.querySelector('[data-atc]')) { e.preventDefault(); self.addForm(form); }
      });
      // quick-add + sticky atc
      document.addEventListener('click', function (e) {
        var q = e.target.closest('[data-quick-add]'); var s = e.target.closest('[data-sticky-atc]');
        if (q) { e.preventDefault(); self.addVariant(q.dataset.variantId, q); }
        else if (s) { e.preventDefault(); self.addVariant(s.dataset.variantId, s); }
      });
      // drawer qty input change
      document.addEventListener('change', function (e) {
        var inp = e.target.closest('[data-qty-input]');
        if (inp && inp.closest('[data-cart-drawer]')) { self.changeLine(inp.closest('[data-cart-item]'), parseInt(inp.value, 10)); }
      });
    },
    open: function () { if (!this.drawer) return; this.drawer.classList.add('is-open'); this.drawer.setAttribute('aria-hidden', 'false'); document.body.style.overflow = 'hidden'; var c = this.drawer.querySelector('[data-cart-close]'); if (c) c.focus(); },
    close: function () { if (!this.drawer) return; this.drawer.classList.remove('is-open'); this.drawer.setAttribute('aria-hidden', 'true'); document.body.style.overflow = ''; },
    busy: function (el, on) { if (!el) return; if (on) { el.setAttribute('aria-busy', 'true'); el.disabled = true; } else { el.removeAttribute('aria-busy'); el.disabled = false; } },
    addForm: function (form) {
      var btn = form.querySelector('[data-atc]'); this.busy(btn, true);
      var self = this;
      fetch(cfg.routes.cartAdd, { method: 'POST', headers: { 'Accept': 'application/json' }, body: new FormData(form) })
        .then(function (r) { return r.json(); })
        .then(function () { return self.refresh(); })
        .then(function () { self.busy(btn, false); self.open(); })
        .catch(function () { self.busy(btn, false); });
    },
    addVariant: function (id, btn) {
      if (!id) return; this.busy(btn, true); var self = this;
      fetch(cfg.routes.cartAdd, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify({ items: [{ id: id, quantity: 1 }] }) })
        .then(function (r) { return r.json(); })
        .then(function () { return self.refresh(); })
        .then(function () { self.busy(btn, false); self.open(); })
        .catch(function () { self.busy(btn, false); });
    },
    step: function (btn, dir) {
      var item = btn.closest('[data-cart-item]'); if (!item) return;
      var input = item.querySelector('[data-qty-input]'); var q = Math.max(0, (parseInt(input.value, 10) || 0) + dir);
      input.value = q;
      if (item.closest('[data-cart-drawer]')) this.changeLine(item, q);
    },
    changeLine: function (item, qty) {
      var line = item.dataset.line; var self = this;
      fetch(cfg.routes.cartChange, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify({ line: parseInt(line, 10), quantity: qty }) })
        .then(function (r) { return r.json(); })
        .then(function (c) { self.render(c); });
    },
    refresh: function () { var self = this; return fetch(cfg.routes.cart + '.js').then(function (r) { return r.json(); }).then(function (c) { self.render(c); return c; }); },
    render: function (c) {
      // header badge
      $$('[data-cart-count]').forEach(function (el) { el.textContent = c.item_count; el.classList.toggle('is-empty', c.item_count === 0); });
      $$('[data-cart-count-text]').forEach(function (el) { el.textContent = c.item_count; });
      $$('[data-cart-count-display]').forEach(function (el) { el.textContent = '(' + c.item_count + ')'; });
      if (!this.drawer) return;
      var body = this.drawer.querySelector('[data-cart-body]');
      var footer = this.drawer.querySelector('[data-cart-footer]');
      if (c.item_count === 0) {
        body.innerHTML = '<div class="cart-drawer__empty"><p>' + (STR.cartEmpty || 'Your cart is empty.') + '</p><a class="button button--secondary" href="' + cfg.routes.cart + '" data-cart-close>' + (STR.cartEmpty ? '' : '') + 'Browse</a></div>';
        if (footer) footer.hidden = true;
        return;
      }
      if (footer) footer.hidden = false;
      var sub = this.drawer.querySelector('[data-cart-subtotal]'); if (sub) sub.textContent = formatMoney(c.total_price);
      var items = c.items.map(function (it, idx) {
        var variant = it.product_has_only_default_variant ? '' : '<p class="cart-drawer__item-variant">' + it.variant_title + '</p>';
        var img = it.image ? '<img class="cart-drawer__item-img" src="' + it.image.replace(/(\.[a-z]+)(\?|$)/i, '_160x$1$2') + '" alt="" width="80" height="94" loading="lazy">' : '';
        return '<li class="cart-drawer__item" data-cart-item data-line="' + (idx + 1) + '" data-key="' + it.key + '">'
          + '<a class="cart-drawer__item-media" href="' + it.url + '">' + img + '</a>'
          + '<div class="cart-drawer__item-info"><a class="cart-drawer__item-title" href="' + it.url + '">' + it.product_title + '</a>' + variant
          + '<div class="cart-drawer__item-controls"><div class="qty qty--sm" data-quantity>'
          + '<button class="qty__btn" type="button" data-qty-down aria-label="−">–</button>'
          + '<input class="qty__input" type="number" value="' + it.quantity + '" min="0" inputmode="numeric" data-qty-input aria-label="' + (STR.quantity || 'Quantity') + '">'
          + '<button class="qty__btn" type="button" data-qty-up aria-label="+">+</button></div>'
          + '<button class="cart-drawer__item-remove" type="button" data-cart-remove>' + (STR.remove || 'Remove') + '</button></div></div>'
          + '<div class="cart-drawer__item-price">' + formatMoney(it.final_line_price) + '</div></li>';
      }).join('');
      body.innerHTML = '<ul class="cart-drawer__items" role="list" data-cart-items>' + items + '</ul>';
    }
  };

  /* ---------- wishlist ---------- */
  var WKEY = 'viaroma:wishlist';
  function getWishlist() { try { return JSON.parse(localStorage.getItem(WKEY) || '[]'); } catch (e) { return []; } }
  function setWishlist(l) { localStorage.setItem(WKEY, JSON.stringify(l)); }
  function inWishlist(id) { return getWishlist().some(function (x) { return String(x.id) === String(id); }); }
  function toggleWishlist(id, handle) {
    var l = getWishlist(); var i = -1;
    l.forEach(function (x, k) { if (String(x.id) === String(id)) i = k; });
    if (i >= 0) l.splice(i, 1); else l.push({ id: id, handle: handle });
    setWishlist(l); markWishlist(); renderWishlistGrid();
  }
  function markWishlist(scope) {
    $$('[data-wishlist-toggle]', scope || document).forEach(function (btn) {
      btn.setAttribute('aria-pressed', inWishlist(btn.dataset.productId) ? 'true' : 'false');
    });
  }
  function renderWishlistGrid() {
    var grid = $('[data-wishlist-grid]'); if (!grid) return;
    var empty = $('[data-wishlist-empty]'); var list = getWishlist();
    grid.innerHTML = '';
    if (!list.length) { if (empty) empty.hidden = false; return; }
    if (empty) empty.hidden = true;
    // Render each card via the Section Rendering API so it reuses the Liquid
    // product-card (with the real Sold/Reserved/1-of-1 badge). Placeholder <li>s
    // are appended in order first, then filled as each fetch resolves.
    list.forEach(function (item) {
      var li = document.createElement('li'); li.className = 'product-grid__item';
      grid.appendChild(li);
      fetch('/products/' + item.handle + '?section_id=wishlist-card')
        .then(function (r) { return r.ok ? r.text() : ''; })
        .then(function (htmlStr) {
          if (!htmlStr) { li.remove(); return; }
          var tmp = document.createElement('div'); tmp.innerHTML = htmlStr;
          var card = tmp.querySelector('.product-card');
          if (card) { li.appendChild(card); markWishlist(li); }
          else { li.remove(); }
        })
        .catch(function () { li.remove(); });
    });
  }

  /* ---------- variant picker ---------- */
  function updateVariant(picker) {
    var json = picker.querySelector('[data-variant-json]'); if (!json) return;
    var variants = JSON.parse(json.textContent);
    var selected = $$('[data-option-position]:checked', picker).map(function (i) { return { pos: parseInt(i.dataset.optionPosition, 10), val: i.value }; });
    selected.forEach(function (s) { var span = picker.querySelector('[data-option-selected="' + s.pos + '"]'); if (span) span.textContent = s.val; });
    var variant = variants.filter(function (v) { return selected.every(function (s) { return v.options[s.pos - 1] === s.val; }); })[0];
    var section = picker.closest('[data-section-type]'); if (!section) return;
    if (variant) {
      var idInput = $('[data-variant-id]', section); if (idInput) idInput.value = variant.id;
      var sticky = $('[data-sticky-atc]', section); if (sticky) sticky.dataset.variantId = variant.id;
      var amt = $('[data-price] .price__amount', section); if (amt) amt.textContent = formatMoney(variant.price);
      var atc = $('[data-atc]', section);
      if (atc) { atc.disabled = !variant.available; var txt = $('[data-atc-text]', atc); if (txt) txt.textContent = variant.available ? (STR.addToCart || 'Add to Cart') : (STR.soldOut || 'Sold'); }
      if (history.replaceState) { var u = new URL(window.location.href); u.searchParams.set('variant', variant.id); history.replaceState({}, '', u.toString()); }
    }
  }

  /* ---------- enquiry form ---------- */
  function submitEnquiry(box, form) {
    var btn = form.querySelector('[type="submit"]'); var orig = btn ? btn.textContent : '';
    if (btn) { btn.disabled = true; btn.textContent = STR.sending || 'Sending…'; }
    fetch(form.action, { method: 'POST', body: new FormData(form), headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function () {
        var s = box.querySelector('[data-enquiry-success]'); if (s) s.hidden = false;
        form.style.display = 'none';
      })
      .catch(function () {
        if (btn) { btn.disabled = false; btn.textContent = orig; }
        var err = document.createElement('p'); err.className = 'enquiry__error'; err.textContent = STR.enquiryError || 'Something went wrong.';
        form.prepend(err);
      });
  }

  /* ---------- facets ---------- */
  function navigateFacets(form) {
    var data = new FormData(form);
    var clean = new URLSearchParams();
    data.forEach(function (v, k) { if (v !== '' && v != null) clean.append(k, v); });
    var base = (form.getAttribute('action') || window.location.pathname).split('?')[0];
    var url = base + (clean.toString() ? '?' + clean.toString() : '');
    if (window.__viaSwup) window.__viaSwup.navigate(url); else window.location.assign(url);
  }

  /* ---------- header ---------- */
  function setupHeader() {
    var header = $('[data-section-type="header"]'); if (!header || header.__hdr) return; header.__hdr = true;

    // sticky
    var mode = header.dataset.sticky;
    if (mode && mode !== 'disabled') {
      header.classList.add('header--sticky');
      var last = window.scrollY; var ticking = false;
      window.addEventListener('scroll', function () {
        if (ticking) return; ticking = true;
        requestAnimationFrame(function () {
          var y = window.scrollY;
          if (mode === 'on-scroll-up') {
            if (y > last && y > 200) header.classList.add('is-hidden'); else header.classList.remove('is-hidden');
          }
          last = y; ticking = false;
        });
      }, { passive: true });
    }

    // mobile / minimal menu
    var menu = $('#MobileMenu'); var toggle = $('[data-menu-toggle]', header);
    if (menu && toggle) {
      var openM = function () { menu.hidden = false; requestAnimationFrame(function () { menu.classList.add('is-open'); }); toggle.setAttribute('aria-expanded', 'true'); document.body.style.overflow = 'hidden'; var c = menu.querySelector('[data-menu-close]'); if (c) c.focus(); };
      var closeM = function () { menu.classList.remove('is-open'); toggle.setAttribute('aria-expanded', 'false'); document.body.style.overflow = ''; setTimeout(function () { menu.hidden = true; }, 400); };
      toggle.addEventListener('click', openM);
      $$('[data-menu-close]', menu).forEach(function (b) { b.addEventListener('click', closeM); });
      $$('[data-accordion-toggle]', menu).forEach(function (btn) {
        btn.addEventListener('click', function () {
          var panel = btn.nextElementSibling; var open = btn.getAttribute('aria-expanded') === 'true';
          btn.setAttribute('aria-expanded', open ? 'false' : 'true'); if (panel) panel.hidden = open;
        });
      });
      document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && menu.classList.contains('is-open')) closeM(); });
    }

    // mega-menu hover intent + keyboard
    $$('[data-nav-item]', header).forEach(function (item) {
      var trigger = $('[data-nav-trigger]', item); if (!trigger) return;
      var t;
      item.addEventListener('mouseenter', function () { t = setTimeout(function () { item.classList.add('is-open'); trigger.setAttribute('aria-expanded', 'true'); }, 100); });
      item.addEventListener('mouseleave', function () { clearTimeout(t); item.classList.remove('is-open'); trigger.setAttribute('aria-expanded', 'false'); });
      trigger.addEventListener('focus', function () { item.classList.add('is-open'); trigger.setAttribute('aria-expanded', 'true'); });
      item.addEventListener('focusout', function (e) { if (!item.contains(e.relatedTarget)) { item.classList.remove('is-open'); trigger.setAttribute('aria-expanded', 'false'); } });
    });

    // search overlay
    var sToggle = $('[data-search-toggle]', header); var overlay = $('#SearchOverlay');
    if (sToggle && overlay) {
      var openS = function () { overlay.hidden = false; var i = overlay.querySelector('[data-predictive-search-input]'); if (i) i.focus(); sToggle.setAttribute('aria-expanded', 'true'); };
      var closeS = function () { overlay.hidden = true; sToggle.setAttribute('aria-expanded', 'false'); };
      sToggle.addEventListener('click', openS);
      $$('[data-search-close]', overlay).forEach(function (b) { b.addEventListener('click', closeS); });
      document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && !overlay.hidden) closeS(); });
      setupPredictive(overlay);
    }

    // currency auto-submit
    $$('[data-currency-selector]', header).forEach(function (sel) { sel.addEventListener('change', function () { var f = sel.closest('form'); if (f) f.submit(); }); });
  }

  function setupPredictive(overlay) {
    var input = overlay.querySelector('[data-predictive-search-input]');
    var results = overlay.querySelector('[data-predictive-search-results]');
    if (!input || !results) return;
    var run = debounce(function () {
      var q = input.value.trim();
      if (q.length < 2) { results.innerHTML = ''; return; }
      fetch(cfg.routes.predictiveSearch + '.json?q=' + encodeURIComponent(q) + '&resources[type]=product,page,article&resources[limit]=6')
        .then(function (r) { return r.json(); })
        .then(function (d) {
          var res = (d.resources && d.resources.results) || {};
          var html = '';
          if (res.products && res.products.length) {
            html += '<div class="predictive__group"><h3 class="predictive__title">' + (cfg.strings.products || 'Products') + '</h3><ul class="predictive__products" role="list">';
            res.products.forEach(function (p) {
              var img = p.featured_image && p.featured_image.url ? '<img src="' + p.featured_image.url + '&width=120" alt="" loading="lazy">' : '';
              html += '<li><a href="' + p.url + '">' + img + '<span class="predictive__p-title">' + p.title + '</span><span class="predictive__p-price">' + (p.price ? formatMoney(parseFloat(p.price) * 100) : '') + '</span></a></li>';
            });
            html += '</ul></div>';
          }
          var misc = (res.pages || []).concat(res.articles || []);
          if (misc.length) {
            html += '<ul class="predictive__misc" role="list">';
            misc.forEach(function (m) { html += '<li><a href="' + m.url + '">' + m.title + '</a></li>'; });
            html += '</ul>';
          }
          if (!html) html = '<p class="predictive__empty">' + (STR.searchNoResults || 'No results found.') + '</p>';
          html += '<a class="predictive__all link-arrow" href="' + cfg.routes.search + '?q=' + encodeURIComponent(q) + '&type=product">' + (cfg.strings.searchAll || 'View all results') + '</a>';
          results.innerHTML = html;
        });
    }, 250);
    input.addEventListener('input', run);
    // arrow-key navigation across result links
    input.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      var links = $$('a', results); if (!links.length) return;
      e.preventDefault();
      var idx = links.indexOf(document.activeElement);
      idx = e.key === 'ArrowDown' ? Math.min(links.length - 1, idx + 1) : Math.max(0, idx - 1);
      links[idx].focus();
    });
  }

  /* ---------- announcement bar ---------- */
  function setupAnnouncement() {
    $$('[data-section-type="announcement-bar"]').forEach(function (bar) {
      if (bar.__ann) return; bar.__ann = true;
      var id = bar.dataset.sectionId;
      if (sessionStorage.getItem('viaroma:announce:' + id) === 'closed') { bar.style.display = 'none'; return; }
      var slides = $$('[data-announcement-slide]', bar);
      if (slides.length > 1 && !reduce.matches) {
        var i = 0; var speed = (parseInt(bar.dataset.rotateSpeed, 10) || 5) * 1000;
        bar.__timer = setInterval(function () { slides[i].classList.remove('is-active'); i = (i + 1) % slides.length; slides[i].classList.add('is-active'); }, speed);
      }
      var close = bar.querySelector('[data-announcement-close]');
      if (close) close.addEventListener('click', function () { bar.style.display = 'none'; if (bar.__timer) clearInterval(bar.__timer); sessionStorage.setItem('viaroma:announce:' + id, 'closed'); });
    });
  }

  /* ---------- collection facet drawer (mobile) ---------- */
  function setupCollectionDrawer() {
    document.addEventListener('click', function (e) {
      var open = e.target.closest('[data-filter-toggle]'); var close = e.target.closest('[data-filter-close]');
      var coll = (open || close) ? (open || close).closest('.collection') : null;
      if (open && coll) { coll.classList.add('is-filters-open'); open.setAttribute('aria-expanded', 'true'); }
      if (close && coll) { coll.classList.remove('is-filters-open'); }
    });
  }

  /* ---------- global delegated events ---------- */
  function setupGlobalDelegation() {
    document.addEventListener('click', function (e) {
      var w = e.target.closest('[data-wishlist-toggle]');
      if (w) { e.preventDefault(); toggleWishlist(w.dataset.productId, w.dataset.productHandle); return; }
      var sh = e.target.closest('[data-share]');
      if (sh) {
        e.preventDefault();
        var data = { title: sh.dataset.shareTitle, url: sh.dataset.shareUrl };
        if (navigator.share) { navigator.share(data).catch(function () {}); }
        else if (navigator.clipboard) { navigator.clipboard.writeText(data.url).then(function () { toast(STR.linkCopied || 'Link copied'); }); }
        return;
      }
      var tab = e.target.closest('[data-tab]');
      if (tab) {
        var wrap = tab.closest('[data-account]'); var name = tab.dataset.tab;
        $$('[data-tab]', wrap).forEach(function (t) { t.setAttribute('aria-selected', t === tab ? 'true' : 'false'); });
        $$('[data-tab-panel]', wrap).forEach(function (p) { p.hidden = p.dataset.tabPanel !== name; });
        if (name === 'wishlist') renderWishlistGrid();
        return;
      }
      var anchor = e.target.closest('[data-scroll-to-enquiry]');
      if (anchor) {
        var target = document.querySelector(anchor.getAttribute('href'));
        if (target) { e.preventDefault(); target.scrollIntoView({ behavior: reduce.matches ? 'auto' : 'smooth', block: 'start' }); var f = target.querySelector('input, textarea'); if (f) setTimeout(function () { f.focus(); }, 400); }
      }
    });

    document.addEventListener('change', function (e) {
      var picker = e.target.closest('[data-variant-picker]'); if (picker) updateVariant(picker);
      var ff = e.target.closest('[data-facets-form]');
      var sort = e.target.matches('[data-sort]') ? e.target : null;
      if (ff || sort) { var form = ff || document.getElementById(sort.getAttribute('form')); if (form) navigateFacets(form); }
    });

    document.addEventListener('submit', function (e) {
      var box = e.target.closest('[data-enquiry]');
      if (box && e.target.matches('form')) { e.preventDefault(); submitEnquiry(box, e.target); }
    });

    // login recover toggle
    document.addEventListener('click', function (e) {
      var t = e.target.closest('[data-recover-toggle]'); if (!t) return;
      var root = t.closest('[data-login]'); if (!root) return;
      var login = root.querySelector('[data-login-panel]'); var recover = root.querySelector('[data-recover-panel]');
      var showRecover = recover.hasAttribute('hidden');
      recover.hidden = !showRecover; login.hidden = showRecover;
    });

    window.addEventListener('storage', function (e) { if (e.key === WKEY) { markWishlist(); renderWishlistGrid(); } });
  }

  /* ---------- Swup ---------- */
  function initSwup() {
    if (!cfg.pageTransitions || reduce.matches) return Promise.resolve();
    return loadScript(cfg.cdn.swup).then(function () {
      if (typeof window.Swup === 'undefined') return;
      var swup = new Swup({ containers: ['#swup'], animationSelector: '[class*="transition-"]', linkSelector: 'a[href]:not([data-no-swup]):not([href^="#"]):not([target="_blank"])' });
      window.__viaSwup = swup;

      swup.hooks.on('visit:start', function () {
        cart.close();
        var menu = $('#MobileMenu'); if (menu) { menu.classList.remove('is-open'); menu.hidden = true; }
        var overlay = $('#SearchOverlay'); if (overlay) overlay.hidden = true;
        document.body.style.overflow = '';
      });

      // GUARANTEE 1: destroy old instances BEFORE init of new ones, on content:replace
      swup.hooks.on('content:replace', function () {
        teardownStickyATC();   // GUARANTEE 3a: disconnect old sticky-ATC observer
        destroySections();     // destroy old component instances first
        pruneScrollTriggers(); // drop triggers whose elements were removed
        setupScope(document);  // then init the new content (re-observes sticky ATC)
        refreshScrollTrigger();// GUARANTEE 2: refresh ScrollTrigger after navigation
      });
    }).catch(function () { /* Swup failed: site remains a normal MPA */ });
  }

  /* ---------- boot ---------- */
  function boot() {
    cart.init();
    setupHeader();
    setupAnnouncement();
    setupCollectionDrawer();
    setupGlobalDelegation();
    setupScope(document);
    renderWishlistGrid();
    initSwup();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
